// src/screens/Projects/ProjectOverview.js
import React, { useEffect, useState, useMemo } from "react";
import "../css/ProjectOverview.css";
import { Nav } from "../../components/nav";
import { useAuth } from "../../navigation/AuthProvider";
import axios from "axios";
import { BACKEND_URL } from "../../constants";
import * as XLSX from "xlsx";

import { calculateProjectHours } from "../../utils/calculateProjectHours";

const months = [
  "Januari", "Februari", "Mars", "April", "Maj", "Juni",
  "Juli", "Augusti", "September", "Oktober", "November", "December"
];

// --- Colors (Timesmart: ljusblå & vit) ---
const TS_COLORS = {
  lightBlue: "#EAF2FF",
  blue: "#2D6AE3",
  border: "#D8E6FF",
  textDark: "#0E2447",
  textMuted: "#5A6B85",
  white: "#FFFFFF",
};

export const ProjectOverview = () => {
  const {
    projects,
    draftSchedule,
    company,
    user,
    overtimeRules,
    absenceRules,
    employees,
    timeSchemas, // ⬅️ add timeschemas from auth
  } = useAuth();

  const now = new Date();
  const defaultMonth = now.getMonth();      // 0-index
  const defaultYear = now.getFullYear();

  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTimeSchema, setSelectedTimeSchema] = useState(""); // ⬅️ NEW: filter by timeschema

  const [allShifts, setAllShifts] = useState(draftSchedule || []);
  const [calculatedData, setCalculatedData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ uppgraderad totalInfo för månaden
  const [totalInfo, setTotalInfo] = useState({
    totalShifts: 0,
    totalHours: 0,
    totalRegularHours: 0,
    totalOvertimeHours: 0,
    totalUniqueEmployees: 0,
    averageHoursPerShift: 0,
  });

  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectPopup, setShowProjectPopup] = useState(false);

  const currentYears = [selectedYear - 1, selectedYear, selectedYear + 1];

  /** ---------- Helpers ---------- */
  const isValidTime = (t) => typeof t === "string" && /^\d{2}:\d{2}$/.test(t);

  const parseTimeToMinutes = (t) => {
    if (!isValidTime(t)) return null;
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };

  // Normalisera intervall [f,t] till samma tidslinje som skiftet [sMin,eMin]
  const normalizeToShiftTimeline = (fromMin, toMin, sMin, eMin) => {
    let f = fromMin;
    let t = toMin;
    if (t <= f) t += 1440; // intervallet korsar midnatt
    if (eMin > sMin && eMin > 1440 && t <= sMin) {
      // skiftet korsar midnatt, och intervallet ligger "före" skiftstart ⇒ flytta till nästa dygn
      f += 1440;
      t += 1440;
    }
    return [f, t];
  };

  const minutesOverlap = (aStart, aEnd, bStart, bEnd) =>
    Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));

  const fmtHM = (mins) => {
    const v = Math.max(0, Math.round(mins));
    const h = Math.floor(v / 60);
    const m = v % 60;
    return `${h}h ${m}m`;
  };

  const toFixed2 = (n) => (Number.isFinite(n) ? n.toFixed(2) : "0.00");

  // Ett pass räknas som komplett för rapporter/lön om: datum, start/slut, projekt & ≥1 employeeId finns
  const isShiftCompleteForReports = (s) => Boolean(
    s &&
    s.status === "complete" &&          // ⬅️ add this
    s.date &&
    isValidTime(s.startTime) &&
    isValidTime(s.endTime) &&
    s.project &&
    Array.isArray(s.employees) &&
    s.employees.some(e => !!e?.employeeId)
  );

  // Räkna arbetade minuter för EN anställd (behåller befintlig logik för totals)
  const getWorkedMinutes = (shift, emp) => {
    const start = parseTimeToMinutes(shift.startTime);
    const endRaw = parseTimeToMinutes(shift.endTime);
    if (start == null || endRaw == null) return 0;

    let end = endRaw <= start ? endRaw + 1440 : endRaw; // hantera över midnatt
    let total = end - start;

    // Raster (per-anställd)
    if (Array.isArray(emp.breaks) && emp.breaks.length > 0) {
      for (const brk of emp.breaks) {
        const bS = parseTimeToMinutes(brk?.from);
        const bEraw = parseTimeToMinutes(brk?.to);
        if (bS == null || bEraw == null) continue;
        const bE = bEraw <= bS ? bEraw + 1440 : bEraw;
        total -= Math.max(0, bE - bS);
      }
    } else {
      // Legacy: skiftnivå-breaks (om de finns)
      const bs = parseTimeToMinutes(shift.breakStartTime);
      const beRaw = parseTimeToMinutes(shift.breakEndTime);
      if (bs != null && beRaw != null) {
        const be = beRaw <= bs ? beRaw + 1440 : beRaw;
        total -= Math.max(0, be - bs);
      }
    }

    // Frånvaro
    if (Array.isArray(emp.absences) && emp.absences.length > 0) {
      if (emp.absences.some(a => a?.appliesWholeShift)) return 0;
      for (const a of emp.absences) {
        const aS = parseTimeToMinutes(a?.from);
        const aEraw = parseTimeToMinutes(a?.to);
        if (aS == null || aEraw == null) continue;
        const aE = aEraw <= aS ? aEraw + 1440 : aEraw;
        const overlapStart = Math.max(start, aS);
        const overlapEnd = Math.min(end, aE);
        total -= Math.max(0, overlapEnd - overlapStart);
      }
    } else if (emp.absence && (emp.absence.appliesWholeShift || (emp.absence.from && emp.absence.to))) {
      if (emp.absence.appliesWholeShift) return 0;
      const aS = parseTimeToMinutes(emp.absence.from);
      const aEraw = parseTimeToMinutes(emp.absence.to);
      if (aS != null && aEraw != null) {
        const aE = aEraw <= aS ? aEraw + 1440 : aEraw;
        const overlapStart = Math.max(start, aS);
        const overlapEnd = Math.min(end, aE);
        total -= Math.max(0, overlapEnd - overlapStart);
      }
    }

    return Math.max(0, total);
  };

  // ✅ NYTT: räkna övertidsminuter för en anställd (hela intervallet)
  const getOvertimeMinutesForEmployee = (shift, emp) => {
    if (!Array.isArray(emp?.overtime) || emp.overtime.length === 0) return 0;

    let total = 0;
    for (const ot of emp.overtime) {
      const oS = parseTimeToMinutes(ot?.from);
      const oEraw = parseTimeToMinutes(ot?.to);
      if (oS == null || oEraw == null) continue;
      const oE = oEraw <= oS ? oEraw + 1440 : oEraw;
      total += Math.max(0, oE - oS);
    }
    return total;
  };

  /** ---------- Snabba upp uppslag ---------- */
  const overtimeRuleById = useMemo(() => {
    const m = {};
    (overtimeRules || []).forEach(r => { m[String(r._id)] = r; });
    return m;
  }, [overtimeRules]);

  const absenceRuleById = useMemo(() => {
    const m = {};
    (absenceRules || []).forEach(r => { m[String(r._id)] = r; });
    return m;
  }, [absenceRules]);

  const employeeNameById = useMemo(() => {
    const m = {};
    (employees || []).forEach(e => {
      m[String(e._id)] = e.name || e.fullName || e.displayName || e.email || String(e._id);
    });
    return m;
  }, [employees]);

  /** ---------- Beräkna projektsammanställning ---------- */
  const calculateProjectData = (shifts) => {
    const filtered = (shifts || []).filter(s => {
      if (!isShiftCompleteForReports(s)) return false;
      const [y, m] = String(s.date).split("-").map(Number);
      if (!(y === selectedYear && m === selectedMonth + 1)) return false;
      if (selectedTimeSchema && String(s.timeSchema) !== String(selectedTimeSchema)) return false; // ⬅️ filter by timeschema
      return true;
    });

    const results = [];
    let totalShifts = 0;

    // ✅ månads-totals
    let totalWorkedMinutesAll = 0;
    let totalOvertimeMinutesAll = 0;
    const uniqueEmployeeIds = new Set();

    for (const project of (projects || [])) {
      const shiftsForProject = filtered.filter(s => s.project === project._id);

      const stats = calculateProjectHours({
        projectId: project._id,
        shifts: shiftsForProject,
        absenceRules,
        overtimeRules,
      });

      const regularHours = stats.totalWorkedMinutes / 60;
      const overtimeHours = stats.totalOvertimeMinutes / 60;
      const hours = (stats.totalWorkedMinutes + stats.totalOvertimeMinutes) / 60;

      results.push({
        projectId: project._id,
        projectName: project.name || "Okänt projekt",
        customerName: (project?.customer && (project.customer.name || project.customer.title))
          || project?.customerName
          || project?.client?.name
          || "",
        totalHours: hours,
        regularHours,
        overtimeHours,
        shiftCount: stats.totalShiftCount,
        employeeCount: stats.employeeCount,
        averageShiftLength: stats.totalShiftCount > 0 ? hours / stats.totalShiftCount : 0,
      });

      totalShifts += stats.totalShiftCount;
      totalWorkedMinutesAll += stats.totalWorkedMinutes;
      totalOvertimeMinutesAll += stats.totalOvertimeMinutes;
    }

    // ✅ unika anställda för hela månaden (alla projekt)
    for (const sh of filtered) {
      for (const e of (sh.employees || [])) {
        if (e?.employeeId) uniqueEmployeeIds.add(String(e.employeeId));
      }
    }

    const totalRegularHours = totalWorkedMinutesAll / 60;
    const totalOvertimeHours = totalOvertimeMinutesAll / 60;
    const totalHours = (totalWorkedMinutesAll + totalOvertimeMinutesAll) / 60;
    const averageHoursPerShift = totalShifts > 0 ? totalHours / totalShifts : 0;

    return {
      results,
      summary: {
        totalShifts,
        totalHours,
        totalRegularHours,
        totalOvertimeHours,
        totalUniqueEmployees: uniqueEmployeeIds.size,
        averageHoursPerShift,
      },
    };
  };

  const updateData = (shifts) => {
    const { results, summary } = calculateProjectData(shifts);
    setCalculatedData(results);
    setTotalInfo(summary);
  };

  /** ---------- Hämta data vid månad/år-byte ---------- */
  useEffect(() => {
    const exists = (allShifts || []).some(s => {
      if (!isShiftCompleteForReports(s)) return false;
      const d = new Date(s.date);
      return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
    });

    if (exists) {
      updateData(allShifts);
      return;
    }

    const fetchShifts = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/get-shifts-for-month`,
          {
            params: {
              userId: user._id,
              companyId: company._id,
              selectedMonth: selectedMonth + 1,
              selectedYear,
            },
          }
        );
        const newShifts = data?.shifts || [];
        const filteredOld = (allShifts || []).filter(s => {
          const d = new Date(s.date);
          return !(d.getMonth() === selectedMonth && d.getFullYear() === selectedYear);
        });
        const merged = [...filteredOld, ...newShifts];
        setAllShifts(merged);
        updateData(merged);
      } catch (err) {
        console.error("Failed to fetch shifts:", err);
      }
      setLoading(false);
    };

    fetchShifts();
  }, [selectedMonth, selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔄 uppdatera beräkning när timeschema ändras (med befintliga skift)
  useEffect(() => {
    updateData(allShifts);
  }, [selectedTimeSchema]); // eslint-disable-line react-hooks/exhaustive-deps

  /** ---------- Exporter ---------- */
  const exportAllProjectsToExcel = () => {
    const period = `${months[selectedMonth]} ${selectedYear}`;
    const personRow = ["Period:", period];
    const blank = [];
    const headers = ["Projekt", "Totalt (h)", "Antal pass", "Ordinarie timmar", "Övertidstimmar", "Snitt/pass (h)"];
    const rows = (calculatedData || []).map(p => [
      p.projectName,
      p.totalHours.toFixed(2),
      p.shiftCount,
      p.regularHours.toFixed(2),
      p.overtimeHours.toFixed(2),
      p.averageShiftLength.toFixed(2)
    ]);
    const sumRow = [
      "TOTALT:",
      totalInfo.totalHours.toFixed(2),
      totalInfo.totalShifts,
      totalInfo.totalRegularHours.toFixed(2),
      totalInfo.totalOvertimeHours.toFixed(2),
      totalInfo.averageHoursPerShift.toFixed(2),
    ];
    const aoa = [
      personRow,
      blank,
      headers,
      ...rows,
      blank,
      sumRow
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Översikt");
    const fileName = `Projekt_översikt_${selectedMonth + 1}_${selectedYear}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const shiftsForSelectedProject = useMemo(() => {
    if (!selectedProject) return [];
    return (allShifts || [])
      .filter(s => {
        if (!isShiftCompleteForReports(s)) return false;
        const [y, m] = String(s.date).split("-").map(Number);
        if (!(s.project === selectedProject.projectId && y === selectedYear && m === selectedMonth + 1)) {
          return false;
        }
        if (selectedTimeSchema && String(s.timeSchema) !== String(selectedTimeSchema)) return false; // ⬅️ same filter in popup
        return true;
      })
      .sort((a, b) => {
        const aDate = String(a.date || "");
        const bDate = String(b.date || "");
        if (aDate < bDate) return -1;
        if (aDate > bDate) return 1;
        return 0;
      });
  }, [selectedProject, allShifts, selectedYear, selectedMonth, selectedTimeSchema]);

  const exportProjectToExcel = (projectId) => {
    const rows = [];
    let projectTotalWorkedMinutes = 0;

    const projectName = projects.find(p => p._id === projectId)?.name || "Okänt projekt";
    const period = `${months[selectedMonth]} ${selectedYear}`;

    for (const sh of shiftsForSelectedProject) {
      const workedMinutesThisShift = (Array.isArray(sh.employees) ? sh.employees : [])
        .filter(e => !!e?.employeeId)
        .reduce((sum, e) => {
          const regular = getWorkedMinutes(sh, e);
          const overtime = getOvertimeMinutesForEmployee(sh, e);
          return sum + regular + overtime;
        }, 0);

      projectTotalWorkedMinutes += workedMinutesThisShift;

      const timeLabel = (isValidTime(sh.startTime) && isValidTime(sh.endTime))
        ? `${sh.startTime}–${sh.endTime}`
        : "—";

      rows.push([
        sh.date,
        timeLabel,
        (workedMinutesThisShift / 60).toFixed(2),
        (Array.isArray(sh.employees) ? sh.employees.filter(e => !!e?.employeeId).length : 0)
      ]);
    }

    const headers = ["Datum", "Tid", "Arbetade timmar (h)", "Antal anställda"];
    const personRow = ["Projekt:", projectName];
    const periodRow = ["Period:", period];
    const blankRow = [];
    const sumRow = [
      "SUMMA:",
      "",
      (projectTotalWorkedMinutes / 60).toFixed(2),
      shiftsForSelectedProject.length
    ];

    const aoa = [
      personRow,
      periodRow,
      blankRow,
      headers,
      ...rows,
      blankRow,
      sumRow
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ProjektData");
    const fileName = `Projekt_${projectName}_${selectedMonth + 1}_${selectedYear}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  /** ---------- UI ---------- */
  return (
    <div className="project-overview-container">
      <Nav />

      <div className="project-overview-page-header">
        <h1>Projektöversikt</h1>
        <div className="project-overview-filter-bar">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {currentYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
          >
            {months.map((month, i) => (
              <option key={month} value={i}>{month}</option>
            ))}
          </select>
          {/* ⬇️ NEW: timeschema-filter, följer befintlig design (en enkel select i filterbaren) */}
          <select
            value={selectedTimeSchema}
            onChange={(e) => setSelectedTimeSchema(e.target.value)}
          >
            <option value="">Alla scheman</option>
            {(timeSchemas || []).map((ts) => (
              <option key={ts._id} value={ts._id}>
                {ts.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="project-overview-search-input"
            placeholder="Sök projekt..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <button
            className="project-overview-export-btn"
            onClick={exportAllProjectsToExcel}
          >
            Exportera alla projekt
          </button>
        </div>
      </div>

      {/* ✅ Mer info i månads-totalen */}
      <div className="project-overview-total">
        {loading ? "Laddar data..." : (
          <>
            <p><strong>Totalt antal pass:</strong> {totalInfo.totalShifts}</p>
            <p><strong>Totala timmar:</strong> {totalInfo.totalHours.toFixed(2)} h</p>
            <p><strong>Ordinarie timmar:</strong> {totalInfo.totalRegularHours.toFixed(2)} h</p>
            <p><strong>Övertidstimmar:</strong> {totalInfo.totalOvertimeHours.toFixed(2)} h</p>
          </>
        )}
      </div>

      <div className="project-overview-table">
        <div className="project-overview-columns">
          <p>Projekt</p>
          <p>Totalt (h)</p>
          <p>Antal pass</p>
          <p>Ordinarie timmar</p>
          <p>Övertidstimmar</p>
          <p>Snitt/pass</p>
        </div>
        {(calculatedData || [])
          .filter(p => p.projectName.toLowerCase().includes(searchQuery.toLowerCase()))
          .sort((a, b) => {
            const byHours = b.totalHours - a.totalHours;
            if (byHours !== 0) return byHours;
            const aCust = (a.customerName || "");
            const bCust = (b.customerName || "");
            return aCust.localeCompare(bCust, 'sv', { sensitivity: 'base' });
          })
          .map(proj => (
            <div
              key={proj.projectId}
              className="project-overview-row"
              onClick={() => {
                setSelectedProject(proj);
                setShowProjectPopup(true);
              }}
              style={{ cursor: "pointer" }}
            >
              <p>{proj.projectName}</p>
              <p>{proj.totalHours.toFixed(2)}</p>
              <p>{proj.shiftCount}</p>
              <p>{proj.regularHours.toFixed(2)}</p>
              <p>{proj.overtimeHours.toFixed(2)}</p>
              <p>{proj.averageShiftLength.toFixed(2)} h</p>
            </div>
          ))}
      </div>

      {showProjectPopup && selectedProject && (
        <div style={popupBackdrop} onClick={() => setShowProjectPopup(false)}>
          <div style={popupModal} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader}>
              <h2 style={{ margin: 0, color: TS_COLORS.textDark }}>{selectedProject.projectName}</h2>
              <p style={{ margin: 0, color: TS_COLORS.textMuted }}>
                Period: <strong>{months[selectedMonth]} {selectedYear}</strong>
              </p>
            </div>

            <div style={summaryRow}>
              <div style={summaryChip}>
                <span style={chipLabel}>Pass</span>
                <span style={chipValue}>{selectedProject.shiftCount}</span>
              </div>
              <div style={summaryChip}>
                <span style={chipLabel}>Totalt (h)</span>
                <span style={chipValue}>{selectedProject.totalHours.toFixed(2)}</span>
              </div>
              <div style={summaryChip}>
                <span style={chipLabel}>Ordinarie timmar</span>
                <span style={chipValue}>{selectedProject.regularHours.toFixed(2)}</span>
              </div>
              <div style={summaryChip}>
                <span style={chipLabel}>Övertidstimmar</span>
                <span style={chipValue}>{selectedProject.overtimeHours.toFixed(2)}</span>
              </div>
              <div style={summaryChip}>
                <span style={chipLabel}>Unika anställda</span>
                <span style={chipValue}>{selectedProject.employeeCount}</span>
              </div>
            </div>

            <h3 style={sectionTitle}>Pass för detta projekt</h3>
            <div className="project-shifts-list" style={shiftList}>
              {shiftsForSelectedProject.length === 0 && (
                <p style={{ fontSize: "14px", color: TS_COLORS.textMuted }}>Inga pass för denna period.</p>
              )}

              {shiftsForSelectedProject.map((sh, idx) => {
                const sMin = parseTimeToMinutes(sh.startTime);
                let eMin = parseTimeToMinutes(sh.endTime);
                if (eMin != null && sMin != null && eMin <= sMin) eMin += 1440;

                const workedMinutesThisShift = (Array.isArray(sh.employees) ? sh.employees : [])
                  .filter(e => !!e?.employeeId)
                  .reduce((sum, e) => {
                    const regular = getWorkedMinutes(sh, e);
                    const overtime = getOvertimeMinutesForEmployee(sh, e);
                    return sum + regular + overtime;
                  }, 0);

                const timeLabel = (isValidTime(sh.startTime) && isValidTime(sh.endTime))
                  ? `${sh.startTime}–${sh.endTime}`
                  : "—";

                const shiftStart = sMin ?? 0;
                const shiftEnd = eMin ?? 0;

                return (
                  <div key={idx} style={shiftCard}>
                    <div style={shiftTopRow}>
                      <div>
                        <div style={badgeRow}>
                          <span style={blueBadge}>Datum</span>
                          <span style={badgeValue}>{sh.date}</span>
                        </div>
                        <div style={badgeRow}>
                          <span style={blueBadge}>Tid</span>
                          <span style={badgeValue}>{timeLabel}</span>
                        </div>
                      </div>
                      <div>
                        <div style={badgeRow}>
                          <span style={blueBadge}>Arbetade timmar (alla)</span>
                          <span style={badgeValue}>
                            {(workedMinutesThisShift / 60).toFixed(2)} h
                          </span>
                        </div>
                        <div style={badgeRow}>
                          <span style={blueBadge}>Anställda</span>
                          <span style={badgeValue}>
                            {(sh.employees || []).filter(e => !!e?.employeeId).length}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                      {(sh.employees || [])
                        .filter(e => !!e?.employeeId)
                        .map((emp, i) => {
                          const empId = String(emp.employeeId);
                          const hasBreaks = Array.isArray(emp.breaks) && emp.breaks.length > 0;
                          const hasOvertime = Array.isArray(emp.overtime) && emp.overtime.length > 0;
                          const hasAbsences = Array.isArray(emp.absences) && emp.absences.some(a => a?.appliesWholeShift || (a?.from && a?.to));
                          const showDetails = hasBreaks || hasOvertime || hasAbsences;

                          const empWorked = getWorkedMinutes(sh, emp);
                          const empOvertime = getOvertimeMinutesForEmployee(sh, emp);
                          const empTotal = empWorked + empOvertime;

                          return (
                            <div key={empId + i} style={empCard}>
                              <div style={empHeader(i % 2 === 0)}>
                                <strong>{employeeNameById[empId] || empId}</strong>
                                <span style={{ marginLeft: 10, color: TS_COLORS.textMuted }}>
                                  · Arbetat: {toFixed2(empTotal / 60)} h
                                </span>
                              </div>

                              {showDetails && (
                                <div style={empBody}>
                                  {hasBreaks && (
                                    <div style={empSection}>
                                      <div style={sectionChip}>Raster</div>
                                      <ul style={ulReset}>
                                        {emp.breaks.map((b, j) => {
                                          const bS = parseTimeToMinutes(b?.from);
                                          const bEraw = parseTimeToMinutes(b?.to);
                                          if (bS == null || bEraw == null) return null;
                                          const [nbS, nbE] = normalizeToShiftTimeline(bS, bEraw, shiftStart, shiftEnd);
                                          const overlap = minutesOverlap(nbS, nbE, shiftStart, shiftEnd);
                                          return (
                                            <li key={j} style={liItem}>
                                              {b.from}–{b.to} <span style={mutedText}>({fmtHM(overlap)})</span>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  )}

                                  {hasOvertime && (
                                    <div style={empSection}>
                                      <div style={sectionChip}>Övertid</div>
                                      <ul style={ulReset}>
                                        {emp.overtime.map((ot, j) => {
                                          const rule = overtimeRuleById[String(ot.type)] || {};
                                          const ruleName = rule.name || "Övertid";
                                          const perc = typeof rule.percentage === "number" ? ` +${rule.percentage}%` : "";
                                          return (
                                            <li key={j} style={liItem}>
                                              {ruleName}{perc}: {ot?.from || "?"}–{ot?.to || "?"}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  )}

                                  {hasAbsences && (
                                    <div style={empSection}>
                                      <div style={sectionChip}>Frånvaro</div>
                                      <ul style={ulReset}>
                                        {emp.absences.map((ab, j) => {
                                          if (!(ab?.appliesWholeShift || (ab?.from && ab?.to))) return null;
                                          const rule = absenceRuleById[String(ab.absenceType)] || {};
                                          const ruleName = rule.name || "Frånvaro";
                                          const percent = typeof rule.percentage === "number" ? `${rule.percentage}%` : "—";

                                          if (ab.appliesWholeShift) {
                                            const dur = fmtHM(Math.max(0, (shiftEnd - shiftStart)));
                                            return (
                                              <li key={j} style={liItem}>
                                                {ruleName} (hela passet) · ersättning: {percent} · tid: {dur}
                                              </li>
                                            );
                                          }

                                          const aS = parseTimeToMinutes(ab?.from);
                                          const aEraw = parseTimeToMinutes(ab?.to);
                                          if (aS == null || aEraw == null) {
                                            return (
                                              <li key={j} style={liItem}>
                                                {ruleName} · ersättning: {percent} · tid: —
                                              </li>
                                            );
                                          }
                                          const [naS, naE] = normalizeToShiftTimeline(aS, aEraw, shiftStart, shiftEnd);
                                          const overlap = minutesOverlap(naS, naE, shiftStart, shiftEnd);
                                          return (
                                            <li key={j} style={liItem}>
                                              {ruleName}: {ab.from}–{ab.to} · ersättning: {percent} ·{" "}
                                              <span style={mutedText}>(tid i pass: {fmtHM(overlap)})</span>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: "1.2rem", textAlign: "center" }}>
              <button
                onClick={() => exportProjectToExcel(selectedProject.projectId)}
                style={downloadButtonStyle}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#205FD6"}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = TS_COLORS.blue}
              >
                Ladda ner Excel
              </button>
              <button
                onClick={() => setShowProjectPopup(false)}
                style={closeBtnStyle}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#BFD4FF"}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = TS_COLORS.lightBlue}
              >
                Stäng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Inline-styles (skapade EN gång för prestanda) ---
const popupBackdrop = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};

const popupModal = {
  backgroundColor: TS_COLORS.white,
  borderRadius: 12,
  padding: 16,
  width: "90vw",
  maxWidth: 1000,
  margin: "0 auto",
  boxShadow: "0 6px 24px rgba(13, 35, 67, 0.15)",
  border: `1px solid ${TS_COLORS.border}`,
};

const modalHeader = {
  backgroundColor: TS_COLORS.lightBlue,
  padding: "14px 16px",
  borderRadius: 8,
  border: `1px solid ${TS_COLORS.border}`,
};

const summaryRow = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 10,
  marginTop: 12,
};

const summaryChip = {
  backgroundColor: TS_COLORS.white,
  border: `1px solid ${TS_COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  textAlign: "center",
  minHeight: 72,
};

const chipLabel = {
  fontSize: 12,
  color: TS_COLORS.textMuted,
  fontWeight: 600,
  letterSpacing: 0.3,
};

const chipValue = {
  fontSize: 18,
  fontWeight: 800,
  color: TS_COLORS.textDark,
  lineHeight: 1,
};

const sectionTitle = {
  marginTop: 16,
  marginBottom: 8,
  fontSize: 18,
  fontWeight: 600,
  color: TS_COLORS.textDark,
};

const shiftList = {
  maxHeight: "60vh",
  overflowY: "auto",
  paddingRight: 8,
  border: `1px solid ${TS_COLORS.border}`,
  borderRadius: 10,
  padding: 12,
  backgroundColor: TS_COLORS.white,
};

const shiftCard = {
  border: `1px solid ${TS_COLORS.border}`,
  background: TS_COLORS.white,
  borderRadius: 10,
  padding: 12,
  marginBottom: 12,
};

const shiftTopRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  flexWrap: "wrap",
};

const badgeRow = { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 };
const blueBadge = {
  background: TS_COLORS.lightBlue,
  border: `1px solid ${TS_COLORS.border}`,
  color: TS_COLORS.textDark,
  padding: "2px 8px",
  fontSize: 12,
  borderRadius: 999,
};
const badgeValue = { color: TS_COLORS.textDark, fontWeight: 600 };

const empCard = {
  border: `1px solid ${TS_COLORS.border}`,
  borderRadius: 10,
  marginTop: 10,
  overflow: "hidden",
  background: TS_COLORS.white,
};

const empHeaderBase = {
  padding: "8px 10px",
  display: "flex",
  alignItems: "center",
  borderBottom: `1px solid ${TS_COLORS.border}`,
};
const empHeader = (even) => ({
  ...empHeaderBase,
  background: even ? TS_COLORS.lightBlue : TS_COLORS.white,
});

const empBody = { padding: "10px 12px", display: "grid", gap: 10 };
const empSection = {
  border: `1px dashed ${TS_COLORS.border}`,
  borderRadius: 8,
  padding: "8px 10px",
};
const sectionChip = {
  display: "inline-block",
  background: TS_COLORS.lightBlue,
  border: `1px solid ${TS_COLORS.border}`,
  color: TS_COLORS.textDark,
  padding: "2px 8px",
  fontSize: 12,
  borderRadius: 999,
  marginBottom: 6,
};

const ulReset = { margin: "6px 0 0 1rem", padding: 0, listStyle: "disc" };
const liItem = { marginBottom: 4, color: TS_COLORS.textDark };
const mutedText = { color: TS_COLORS.textMuted };

const downloadButtonStyle = {
  padding: "10px 20px",
  fontSize: "16px",
  backgroundColor: TS_COLORS.blue,
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.2s",
};

const closeBtnStyle = {
  padding: "10px 20px",
  fontSize: "16px",
  backgroundColor: TS_COLORS.lightBlue,
  color: TS_COLORS.textDark,
  border: `1px solid ${TS_COLORS.border}`,
  borderRadius: "8px",
  cursor: "pointer",
  marginLeft: 8,
  transition: "background-color 0.2s",
};
